import type { LocationDisplayData } from "./displayedFieldUtils";

export type MapNavigationAppId = "google-maps" | "apple-maps" | "waze";

export interface MapNavigationOption {
  id: MapNavigationAppId;
  labelKey: "googleMaps" | "appleMaps" | "waze";
  url: string;
}

export function buildLocationSearchQuery(
  location: LocationDisplayData,
): string {
  const lineCodePostalCommuneCedex = [
    location.codePostal,
    location.communeNom,
    location.cedex,
  ]
    .filter(Boolean)
    .join(" ");

  return [
    location.nomDuLieu,
    location.adresse1,
    location.adresse2,
    location.adresse3,
    lineCodePostalCommuneCedex,
    location.bureauDistribution,
    location.paysLabel,
  ]
    .filter(Boolean)
    .join(", ");
}

export type MapNavigationMode = "address" | "coordinates";

export function hasValidLatLng(lat?: number, lng?: number): boolean {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  );
}

/** Coordonnées GeoJSON Apidae : `[longitude, latitude]`. */
export function hasValidCoordinates(
  coordinates?: [number, number] | null,
): boolean {
  return (
    Array.isArray(coordinates) &&
    coordinates.length >= 2 &&
    hasValidLatLng(coordinates[1], coordinates[0])
  );
}

export function parseGeoJsonCoordinates(
  coordinates?: [number, number] | null,
): { lat: number; lng: number } | null {
  if (!hasValidCoordinates(coordinates)) {
    return null;
  }
  const [lng, lat] = coordinates!;
  return { lat, lng };
}

export function getMapNavigationOptions(params: {
  query: string;
  coordinates?: [number, number] | null;
  label?: string;
  mode?: MapNavigationMode;
}): MapNavigationOption[] {
  const { query, coordinates, label, mode = "address" } = params;
  const encodedQuery = encodeURIComponent(query);
  const encodedLabel = encodeURIComponent(label || query);
  const parsedCoords = parseGeoJsonCoordinates(coordinates);
  const hasCoords = parsedCoords !== null;

  const useCoords = mode === "coordinates" && hasCoords;

  if (!useCoords && !query) {
    // Fallback to coords if no address text
    if (hasCoords) {
      return buildCoordsOptions(
        parsedCoords.lat,
        parsedCoords.lng,
        encodedLabel,
      );
    }
    return [];
  }

  if (useCoords) {
    return buildCoordsOptions(
      parsedCoords!.lat,
      parsedCoords!.lng,
      encodedLabel,
    );
  }

  return [
    {
      id: "google-maps",
      labelKey: "googleMaps",
      url: `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`,
    },
    {
      id: "apple-maps",
      labelKey: "appleMaps",
      url: `https://maps.apple.com/?q=${encodedQuery}`,
    },
    {
      id: "waze",
      labelKey: "waze",
      url: `https://www.waze.com/ul?q=${encodedQuery}&navigate=yes`,
    },
  ];
}

function buildCoordsOptions(
  lat: number,
  lng: number,
  encodedLabel: string,
): MapNavigationOption[] {
  return [
    {
      id: "google-maps",
      labelKey: "googleMaps",
      url: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    },
    {
      id: "apple-maps",
      labelKey: "appleMaps",
      url: `https://maps.apple.com/?ll=${lat},${lng}&q=${encodedLabel}`,
    },
    {
      id: "waze",
      labelKey: "waze",
      url: `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    },
  ];
}

export function canOpenLocationInMapApp(
  location: LocationDisplayData,
  coordinates?: [number, number] | null,
): boolean {
  const query = buildLocationSearchQuery(location);
  return !!query || hasValidCoordinates(coordinates);
}
